import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { B2BUnitService } from '@b2b/features/b2b-unit/core/b2b-unit.service';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { B2B_UNIT_ID_KEY } from '@shared/tools/constants';
import { getOrCreateCart } from '@b2b/features/cart/core/store/cart.actions';
import { B2BUnit } from '@b2b/features/b2b-unit/core/b2b-unit.model';

@Component({
  selector: 'app-select-b2b-unit-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './select-b2b-unit-modal.component.html',
  styleUrls: ['./select-b2b-unit-modal.component.scss'],
})
export class SelectB2bUnitModalComponent implements OnInit, OnDestroy {
  b2bUnits: B2BUnit[] = [];
  filteredB2BUnits: B2BUnit[] = [];
  selectedB2BUnit: B2BUnit | null = null;
  searchTerm = '';
  isLoading = false;
  preselectedB2bUnitId?: string;
  private destroy$ = new Subject<void>();

  constructor(
    private b2bUnitService: B2BUnitService,
    private store: Store,
    private modalService: ModalService<{ preselectedB2bUnitId?: string }>,
  ) {}

  ngOnInit(): void {
    this.modalService
      .getDataByModalID('select-b2b-unit-modal')
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data?.preselectedB2bUnitId) {
          this.preselectedB2bUnitId = data.preselectedB2bUnitId;
        }
      });

    this.searchB2BUnits();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchB2BUnits(): void {
    this.isLoading = true;
    this.b2bUnitService
      .searchB2Bunits({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.b2bUnits = response.data;
          this.filteredB2BUnits = response.data;

          if (this.preselectedB2bUnitId) {
            this.selectedB2BUnit = this.b2bUnits.find(unit => unit.uid === this.preselectedB2bUnitId) || null;
          }

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  onSearch(): void {
    const trimmedSearch = this.searchTerm.trim();

    if (!trimmedSearch) {
      this.searchB2BUnits();
      return;
    }

    this.isLoading = true;
    const searchParams = {
      term: trimmedSearch,
    };

    this.b2bUnitService
      .searchB2Bunits(searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.filteredB2BUnits = response.data;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  selectB2BUnit(unit: B2BUnit): void {
    this.selectedB2BUnit = unit;
  }

  confirmSelection(): void {
    if (!this.selectedB2BUnit) {
      return;
    }

    localStorage.setItem(B2B_UNIT_ID_KEY, this.selectedB2BUnit.uid);
    this.b2bUnitService.addB2BUnitToStore(this.selectedB2BUnit.uid);
    this.store.dispatch(getOrCreateCart());
    this.close();
  }

  close(): void {
    this.modalService.close(['select-b2b-unit-modal']);
  }
}
